import { createFileRoute, redirect } from '@tanstack/react-router';
import { buildAbility } from '@tronche2tek/shared-types';
import { useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { meQueryOptions } from '../../../../api/me';
import { uploadUserPhoto } from '../../../../api/users';
import { CropModal } from '../../../../components/photo/CropModal';
import type { Capture } from '../../../../components/photo/webcam/WebcamCapture';
import { WebcamCapture } from '../../../../components/photo/webcam/WebcamCapture';
import { UserSearch } from '../../../../components/users/UserSearch';
import type { User } from '../../../../types';

const DEVICE_KEY = 'kiosk.cameraDeviceId';

export const Route = createFileRoute('/_auth/photos/kiosk/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.query({
      ...meQueryOptions(),
      staleTime: 'static',
    });
    if (!buildAbility(me.role).can('manage', 'Photo')) {
      throw redirect({ to: '/' });
    }
  },
  component: KioskPage,
});

/**
 * A frozen frame plus the person it was taken of. The target is bound here, at
 * shutter time, and never re-read from render state afterwards: everything
 * downstream (the crop modal, the send, a retried send) carries this object,
 * so a selection change while the modal is open cannot re-point the photo at
 * somebody else's Entra profile.
 */
interface Pending {
  capture: Capture;
  url: string;
  target: User;
}

/**
 * Only the tail of a completed send. Sending shows in the crop modal, which
 * stays up for the duration, and a failure shows there too — next to the photo
 * that failed, with the button that retries it. Neither needs a home here.
 */
type Status =
  | { kind: 'idle' }
  | { kind: 'sent'; name: string }
  | { kind: 'pending'; name: string };

function KioskPage() {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  // Revokes the pending object URL on unmount. An effect event reads the
  // current value, so the cleanup runs once (empty deps) rather than on every
  // change — the confirm/cancel/re-capture paths already revoke the URLs they
  // drop, and doing it again would double-revoke.
  const revokePending = useEffectEvent(() => {
    if (pending) {
      URL.revokeObjectURL(pending.url);
    }
  });

  useEffect(() => {
    return () => {
      revokePending();
    };
  }, []);

  function onCapture(capture: Capture) {
    // The shutter (and its escape-hatch override) are locked via `disabled`
    // whenever no one is selected, so this should be unreachable with
    // `user === null`. Guarded anyway: WebcamCapture stays mounted across
    // the whole session now, so a capture must never attach to nobody.
    const target = user;
    if (!target) {
      return;
    }
    if (pending) {
      URL.revokeObjectURL(pending.url);
    }
    setPending({ capture, url: URL.createObjectURL(capture.blob), target });
  }

  function discardPending() {
    if (pending) {
      URL.revokeObjectURL(pending.url);
    }
    setPending(null);
  }

  // Changing who is selected discards a capture that has not been confirmed
  // yet. It could safely be kept — it is bound to its own target and would
  // still be sent to the right person — but an open crop modal next to
  // somebody else's name is a trap, and an operator who reaches for the
  // search box mid-crop has moved on. Re-selecting the same person is not a
  // change and keeps the capture.
  function handleSelectUser(next: User | null) {
    if (pending && pending.target.id !== next?.id) {
      discardPending();
    }
    setUser(next);
  }

  // "Done with this person": abandon mid-flow without sending, without
  // touching the (kept-alive) stream, and without leaving a stale status
  // line sitting next to the next person's fresh selection.
  function handleNext() {
    discardPending();
    setUser(null);
    setStatus({ kind: 'idle' });
  }

  /**
   * Upload one confirmed photo. Throws on failure, deliberately: the caller is
   * the crop modal's `onConfirm`, which stays open and shows the error next to
   * the photo that failed. Swallowing it here would close the modal over a
   * photo that never landed.
   *
   * The operator waits for this. An earlier version uploaded in the background
   * so they could photograph the next person immediately, which needed a queue,
   * a failure list and a retry control — and the confirmation only ever arrived
   * after the fact, if it arrived. Waiting costs a second and makes the
   * photo-that-ceases-to-exist bug unrepresentable: this blob lives in exactly
   * one place, and `discardPending` runs only after the server has it.
   */
  async function send(blob: Blob, target: User) {
    const ack = await uploadUserPhoto(target.id, blob, {
      targetUpn: target.userPrincipalName,
      targetName: target.displayName,
    });
    // Cible salariée (Lucca) : le serveur n'a pas poussé la photo vers Entra,
    // il a créé une demande qu'un AUTRE opérateur devra approuver — le statut
    // doit le dire, pas annoncer un envoi qui n'a pas eu lieu.
    setStatus({
      kind: ack.status === 'pending' ? 'pending' : 'sent',
      name: target.displayName,
    });
    // Nothing raced this any more: the modal held the operator until the server
    // answered, so whoever is selected now is still `target`.
    setUser(null);
  }

  return (
    <section className="epi-card tone-blue">
      <div className="epi-card-head">
        <span className="num">S</span>
        <span className="title">{t('photoKiosk.title')}</span>
        <span className="meta">{t('photoKiosk.meta')}</span>
      </div>

      <div className="flex flex-col gap-5 p-6">
        <UserSearch onSelectUser={handleSelectUser} selectedUser={user} />

        {!user && (
          <p className="text-sm text-muted">{t('photoKiosk.selectUser')}</p>
        )}

        <WebcamCapture
          onCapture={onCapture}
          deviceStorageKey={DEVICE_KEY}
          disabled={!user}
        />

        {user && (
          <button
            type="button"
            onClick={handleNext}
            className="btn-link self-start"
          >
            {t('photoKiosk.next')} →
          </button>
        )}

        {/*
          The live status appears after the crop dialog has unmounted, so focus
          is nowhere near it and a screen reader would otherwise never hear that
          the upload finished.
        */}
        <div aria-live="polite" aria-atomic="true">
          {status.kind === 'sent' && (
            <p className="label text-green">
              {t('photoKiosk.sent', { name: status.name })}
            </p>
          )}
          {status.kind === 'pending' && (
            <p className="label text-blue">
              {t('photoKiosk.pendingApproval', { name: status.name })}
            </p>
          )}
        </div>
      </div>

      {/*
        Rendered on `pending` alone. It deliberately does NOT also test `user`:
        a guard that depends on the current selection hides the modal without
        discarding the capture inside it, which is how a photo of one person
        ends up confirmed under another's name.
      */}
      {pending && (
        <CropModal
          src={pending.url}
          initialCrop={pending.capture.crop}
          // Confirming here uploads, so this dialog is the last place anyone
          // can look at the photo. The crop rectangle says which square was
          // picked; it does not say whether the eyes are open.
          previewBeforeConfirm
          onCancel={discardPending}
          onConfirm={async (blob) => {
            // Awaited, so the modal stays up for the upload and shows its
            // failure. `discardPending` runs only if this resolved — the blob
            // is not thrown away until the server has taken it.
            await send(blob, pending.target);
            discardPending();
          }}
        />
      )}
    </section>
  );
}
